// ArticleCard - Public article preview card

import React from 'react';
import {
  Calendar,
  User,
  ArrowRight,
  MapPin,
  Heart,
  Briefcase,
  Sparkles,
  ExternalLink,
  Newspaper,
  Link as LinkIcon
} from 'lucide-react';
import { ARTICLE_CATEGORIES, type Article } from '../../services/articlesService';

// Icon-Mapping für Kategorien
const CategoryIcon: React.FC<{ iconName: string; className?: string }> = ({ iconName, className = "w-4 h-4" }) => {
  const icons: Record<string, React.ReactNode> = {
    'MapPin': <MapPin className={className} />,
    'Heart': <Heart className={className} />,
    'Briefcase': <Briefcase className={className} />,
    'Sparkles': <Sparkles className={className} />,
    'ExternalLink': <ExternalLink className={className} />,
    'Newspaper': <Newspaper className={className} />,
  };
  return <>{icons[iconName] || null}</>;
};

interface ArticleCardProps {
  article: Article;
  onClick?: () => void;
  variant?: 'default' | 'featured' | 'compact' | 'link';
}

export function ArticleCard({ article, onClick, variant = 'default' }: ArticleCardProps) {
  const category = ARTICLE_CATEGORIES.find((c) => c.value === article.category);

  const formatDate = (date: any) => {
    if (!date) return '';
    let d: Date;
    if (date.toDate && typeof date.toDate === 'function') {
      d = date.toDate();
    } else if (date.seconds) {
      // Handle serialized Firestore Timestamp (from JSON.parse)
      d = new Date(date.seconds * 1000);
    } else if (date instanceof Date) {
      d = date;
    } else {
      d = new Date(date);
    }
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('de-DE', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  // News variant - zeigt komplettes Bild ohne Zuschnitt
  if (article.category === 'news' && !article.isExternal) {
    return (
      <article
        onClick={onClick}
        className="group bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg hover:border-indigo-300 transition cursor-pointer"
      >
        {/* Cover Image - object-contain fuer volles Bild */}
        {article.coverImage && (
          <div className="aspect-[16/9] overflow-hidden bg-gray-100">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
            />
          </div>
        )}

        <div className="p-4">
          {/* Header mit Badge */}
          <div className="flex items-center justify-between mb-3">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-indigo-50 text-indigo-700 text-xs font-medium rounded-full">
              <Newspaper className="w-3 h-3" />
              News
            </span>
            <span className="text-xs text-gray-500">
              {formatDate(article.publishedAt)}
            </span>
          </div>

          {/* Title */}
          <h3 className="text-base font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-indigo-600 transition">
            {article.title}
          </h3>

          {/* Content Preview */}
          {article.content && (
            <p className="text-gray-600 text-sm line-clamp-3 mb-3">
              {article.content}
            </p>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <User className="w-3.5 h-3.5" />
              {article.author}
            </span>
            <span className="flex items-center gap-1 text-indigo-600 font-medium">
              Weiterlesen
              <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </div>
      </article>
    );
  }

  // Link variant - kompakte Karte für externe Links
  if (variant === 'link' || article.isExternal) {
    return (
      <article
        onClick={onClick}
        className="group bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg hover:border-indigo-300 transition cursor-pointer"
      >
        {/* Cover Image (wenn vorhanden) */}
        {article.coverImage && (
          <div className="aspect-[16/9] overflow-hidden">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          </div>
        )}

        <div className="p-4">
          {/* Header mit Badge */}
          <div className="flex items-center justify-between mb-3">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-700 text-xs font-medium rounded-full">
              <LinkIcon className="w-3 h-3" />
              Externer Link
            </span>
            {category && (
              <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                <CategoryIcon iconName={category.icon} className="w-3.5 h-3.5" />
                {category.label}
              </span>
            )}
          </div>

          {/* Title */}
          <h3 className="text-base font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-indigo-600 transition">
            {article.title}
          </h3>

          {/* Excerpt */}
          {article.excerpt && (
            <p className="text-gray-600 text-sm line-clamp-2 mb-3">
              {article.excerpt}
            </p>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>{formatDate(article.publishedAt)}</span>
            <span className="flex items-center gap-1 text-indigo-600 font-medium group-hover:gap-2 transition-all">
              Öffnen
              <ExternalLink className="w-3.5 h-3.5" />
            </span>
          </div>
        </div>
      </article>
    );
  }

  if (variant === 'featured') {
    return (
      <article
        onClick={onClick}
        className="group relative overflow-hidden rounded-2xl cursor-pointer"
      >
        {/* Background Image */}
        <div className="aspect-[16/9] md:aspect-[21/9]">
          {article.coverImage ? (
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-primary to-secondary" />
          )}
        </div>

        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />

        {/* Content */}
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
          {/* Category Badge */}
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm mb-3">
            {category && <CategoryIcon iconName={category.icon} className="w-4 h-4" />}
            <span>{category?.label}</span>
          </div>

          <h2 className="text-2xl md:text-3xl font-bold text-white mb-2 group-hover:text-indigo-600-light transition">
            {article.title}
          </h2>
          <p className="text-white/80 line-clamp-2 mb-4 max-w-2xl">
            {article.excerpt}
          </p>

          <div className="flex items-center gap-4 text-white/60 text-sm">
            <span className="flex items-center gap-1">
              <User className="w-4 h-4" />
              {article.author}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {formatDate(article.publishedAt)}
            </span>
          </div>
        </div>
      </article>
    );
  }

  if (variant === 'compact') {
    return (
      <article
        onClick={onClick}
        className="group flex gap-4 p-3 rounded-xl hover:bg-gray-50 cursor-pointer transition"
      >
        {/* Thumbnail */}
        <div className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden">
          {article.coverImage ? (
            <img
              src={article.coverImage}
              alt=""
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
              {category && <CategoryIcon iconName={category.icon} className="w-6 h-6 text-gray-400" />}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
            {category && <CategoryIcon iconName={category.icon} className="w-3.5 h-3.5" />}
            {category?.label}
          </div>
          <h3 className="font-semibold text-gray-900 line-clamp-2 group-hover:text-indigo-600 transition">
            {article.title}
          </h3>
        </div>
      </article>
    );
  }

  // Default variant
  return (
    <article
      onClick={onClick}
      className="group bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition cursor-pointer"
    >
      {/* Cover Image */}
      <div className="aspect-[16/10] overflow-hidden">
        {article.coverImage ? (
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center">
            {category && <CategoryIcon iconName={category.icon} className="w-10 h-10 text-gray-300" />}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5">
        {/* Category */}
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-indigo-500/10 text-indigo-600 text-xs font-medium rounded-full">
            {category && <CategoryIcon iconName={category.icon} className="w-3.5 h-3.5" />}
            {category?.label}
          </span>
        </div>

        {/* Title */}
        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-indigo-600 transition">
          {article.title}
        </h3>

        {/* Excerpt */}
        <p className="text-gray-600 text-sm line-clamp-2 mb-4">
          {article.excerpt}
        </p>

        {/* Meta */}
        <div className="flex items-center justify-between text-sm text-gray-500">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <User className="w-4 h-4" />
              {article.author}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {formatDate(article.publishedAt)}
            </span>
          </div>
          <ArrowRight className="w-4 h-4 text-indigo-600 opacity-0 group-hover:opacity-100 transition" />
        </div>
      </div>
    </article>
  );
}

export default ArticleCard;
